import React, { useMemo } from 'react';
import './MilestoneDates.css';

const ARROW_ICON_URL = "https://storage.googleapis.com/keyflo-public-assets/keyflo-icon-white.png";

const MilestoneDates = ({ data, isExpanded = false, onToggle, calendarLink }) => {

  // Parse date string as local date (no timezone conversion)
  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      return new Date(dateString);
    } catch {
      return null;
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = parseLocalDate(dateStr);
      if (!date || isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  const milestones = useMemo(() => {
    if (!data || !data.milestone_dates) return [];

    // Filter and sort (backend also omits non-key milestones).
    let list = data.milestone_dates;

    // Sort by date
    list.sort((a, b) => {
      const dA = parseLocalDate(a.date);
      const dB = parseLocalDate(b.date);
      return (dA || 0) - (dB || 0);
    });

    return list;
  }, [data]);

  // Determine status for each milestone
  const processedMilestones = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Find first incomplete milestone
    let activeIndex = -1;
    for (let i = 0; i < milestones.length; i++) {
      // Check if status is NOT complete (active or incomplete or anything else)
      if (milestones[i].status !== 'complete') {
        activeIndex = i;
        break;
      }
    }

    return milestones.map((m, index) => {
      const mDate = parseLocalDate(m.date);
      const nameLower = (m.display_name || '').toLowerCase();

      // Days difference
      let daysDiff = 0;
      if (mDate) {
        mDate.setHours(0, 0, 0, 0);
        const diffTime = mDate - today;
        daysDiff = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      }

      let status = 'future';

      // Prioritize explicit status from API
      if (m.status === 'complete') {
        status = 'completed';
      } else if (index === activeIndex) {
        status = 'active';
      }
      // Else remains 'future' (visualized as incomplete)

      const isClosing = nameLower.includes('close of escrow');

      const isOverdue = status === 'active' && daysDiff < 0;
      const daysOverdue = Math.abs(daysDiff);

      return {
        ...m,
        status, // completed, active, future
        isClosing,
        daysDiff,
        isOverdue,
        daysOverdue
      };
    });
  }, [milestones]);

  if (!data) return null;

  return (
    <div className="milestone-dates">
      <div className="milestone-card">
        {/* Card Header - Always visible, acts as toggle */}
        <div className="milestone-card-header" onClick={onToggle} style={{ cursor: onToggle ? 'pointer' : 'default' }}>
          <h3 className="milestone-card-title">Key Milestones</h3>

          <div className="milestone-header-actions">
            {calendarLink && (
              <a
                href={calendarLink}
                target="_blank"
                rel="noopener noreferrer"
                className="calendar-subscribe-btn"
                onClick={(e) => e.stopPropagation()}
                title="Subscribe to Calendar"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                Subscribe to Calendar
              </a>
            )}

            {/* Expand/collapse chevron */}
            {onToggle && (
            <button
              className="expand-button-chevron"
              onClick={(e) => { e.stopPropagation(); onToggle(); }}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
          )}
          </div>
        </div>

        {/* List - Only visible when expanded */}
        {isExpanded && (
          <div className="milestone-list">
            {processedMilestones.length === 0 && (
              <div className="no-milestones">No milestones found.</div>
            )}

            {processedMilestones.map((m, idx) => {
              const classes = ['milestone-row'];
              if (m.status === 'completed') classes.push('is-completed');
              if (m.status === 'active') classes.push('is-active');
              if (m.isOverdue) classes.push('is-overdue');
              if (m.isClosing && m.status !== 'active' && m.status !== 'completed') classes.push('is-closing');
              if (m.isClosing) classes.push('is-closing-base');

              return (
                <div key={idx} className={classes.join(' ')}>
                  <div className="milestone-date-col">
                    {formatDate(m.date)}
                  </div>
                  <div className="milestone-text-col">
                    {m.display_name}
                    {m.status === 'completed' && (
                      <span className="status-badge-complete">✓ Complete</span>
                    )}
                    {m.status === 'active' && !m.isOverdue && (
                      <span className="status-badge-due">
                        (Due {m.daysDiff === 0 ? 'Today' : (m.daysDiff === 1 ? 'Tomorrow' : `in ${m.daysDiff} days`)})
                      </span>
                    )}
                    {m.status === 'active' && m.isOverdue && (
                      <span className="status-badge-overdue">
                        Over due by {m.daysOverdue} days!
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MilestoneDates;